import { Box, Button, ClickAwayListener, FormControl, Menu, MenuItem, OutlinedInput, Tooltip } from '@mui/material';
import { MultiSelectFilter } from '../MultiSelectFilter';
import { CustomDatePicker } from '../CustomDatePicker';
import { MaterialMap } from '@consts/materials';
import { CutType } from '@api/cut';
import { useEffect, useMemo, useRef, useState } from 'react';
import { OrgUrls, fetchOrgGeneric } from '@api/org';
import { differenceObjects } from '@utils/data';
import { CutFilterOptions, CutTypeMap } from '@/types/cuts';
import { useFetch } from '@hooks/useFetch';
import { useSelector } from 'react-redux';
import { ArrowDropDown } from '@mui/icons-material';
import { SearchIDType, TYPES } from '@consts/cuts';
import { useStyles } from './styles/CutActivityFilters.styles';
import { isFilterSelected, cascadeOptions, newDisabler } from '@utils/filters';

import type { CutFilter } from '@/types/cuts';
import type { IdMap } from '@/api/org';
import type { RootState } from '@features/redux/store';
import type { OrgsState } from '@features/redux/slices/orgs';
import { designAPI } from '@api/designs';

/** static data, avoids re-renders & prevents bug with reordering in list **/
const CutTypeOptions = Object.values(CutType);

const MaterialOptions = Object.keys(MaterialMap).map((key) => ({
    materialId: (MaterialMap as any)[key],
    material: key,
}));

const shouldDisable = newDisabler([CutFilterOptions.Company, CutFilterOptions.Reseller, CutFilterOptions.Location]);

interface CutActivityFiltersProps {
    orgs: OrgsState;
    dateRange: { startDate: string; endDate: string };
    searchIDType: SearchIDType;
    searchID: string;
    searchIDError: string;
    setSearchIDError: (str: string) => void;
    filters: CutFilter;
    dateRangeError: string;
    companies: IdMap;
    locations: IdMap;
    resellers: IdMap;
    setFilter: (key: CutFilterOptions, value: string, selectOne?: boolean) => void;
    setDateRange: (range: { startDate: string; endDate: string }) => void;
    setSearchIDType: (type: SearchIDType) => void;
    setSearchID: (cutId: string) => void;
    onSearch: () => void;
}

export const CutActivityFilters = ({
    orgs,
    dateRange,
    searchIDType,
    searchID,
    filters,
    dateRangeError,
    companies,
    locations,
    resellers,
    setFilter,
    setDateRange,
    setSearchIDType,
    setSearchID,
    searchIDError,
    setSearchIDError,
    onSearch,
}: CutActivityFiltersProps) => {
    const classes = useStyles();

    const { IdToken } = useSelector(({ auth }: RootState) => auth);

    const [idSelectOpen, setIdSelectOpen] = useState(false);

    const cutIdAnchor = useRef<HTMLButtonElement | null>(null);

    const { data: countriesData } = useFetch({
        fetcher: () => fetchOrgGeneric<{ countryName: string }[]>(OrgUrls.Countries)(IdToken!),
        defaultValue: [],
        modifier: (countries) => countries.map(({ countryName }) => countryName).sort(),
    });

    const { data: categoryData } = useFetch({
        fetcher: () => designAPI(IdToken!).getCategories(),
        defaultValue: [],
        modifier: (categories) => categories.map(({ categoryName }) => categoryName).sort(),
    });

    const companyValues = useMemo(
        () => Object.entries(companies).map(([key, value]) => ({ companyId: key, companyName: value })),
        [companies]
    );

    /** This could have less iteration/traversal.  */
    const resellerValues = useMemo(() => {
        const options = Object.entries(differenceObjects(companies, resellers)).map(([key, value]) => ({
            resellerId: key,
            resellerName: value,
        }));

        return cascadeOptions({
            options,
            parent: filters.companyIds,
            map: orgs.companyResellerMap,
            idKey: 'resellerId',
        });
    }, [resellers, filters.companyIds]);

    const locationValues = useMemo(() => {
        const byReseller = isFilterSelected(filters, CutFilterOptions.Reseller);

        const options = Object.entries(locations).map(([key, value]) => ({
            locationId: key,
            locationName: `#${key} - ${value}`,
        }));

        return cascadeOptions({
            options,
            parent: byReseller ? filters.resellerIds : filters.companyIds,
            map: byReseller ? orgs.resellerLocationMap : orgs.companyLocationMap,
            idKey: 'locationId',
        });
    }, [locations, filters.resellerIds, filters.companyIds, filters.locationIds]);

    const [selectedCutType] = useMemo(
        () => Object.entries(filters.type).find(([, value]) => value) || [],
        [filters.type]
    );

    const statusOptions = useMemo(
        () => (selectedCutType ? CutTypeMap[selectedCutType as CutType] : []),
        [selectedCutType]
    );

    const isSelected = (key: CutFilterOptions) => {
        const selected = filters[key];
        return Object.values(selected).some((enabled) => enabled);
    };

    const validateSearchId = (value: string) => {
        if (TYPES[searchIDType].validator(value) || value === '') {
            setSearchIDError('');
            return;
        }

        setSearchIDError(`Invalid ${TYPES[searchIDType].label} provided!`);
    };

    useEffect(() => {
        validateSearchId(searchID);
    }, [searchID, searchIDType]);

    return (
        <div className={classes.container}>
            <Box width='100%' display='flex' flexWrap='wrap'>
                <div style={{ marginLeft: '10px' }}>
                    <CustomDatePicker
                        disabled={Boolean(searchID)}
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        buttonSize={{ width: 150, height: 30 }}
                        dateRangeError={dateRangeError}
                    />
                </div>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (!searchIDError) onSearch();
                    }}>
                    <FormControl variant='standard' sx={{ width: '310px', display: 'flex', flexWrap: 'wrap' }}>
                        <Tooltip title={searchIDError} placement='top' arrow open={true}>
                            <OutlinedInput
                                id='input-with-icon-adornment'
                                className={classes.textInput}
                                placeholder={`Ex. ${TYPES[searchIDType].example}`}
                                onChange={(e) => setSearchID(e.target.value)}
                                value={searchID}
                                inputProps={{
                                    maxLength: 35,
                                }}
                                autoComplete='off'
                                endAdornment={
                                    <ClickAwayListener
                                        disableReactTree={true}
                                        onClickAway={() => setIdSelectOpen(false)}>
                                        <div>
                                            <Button
                                                variant={'text'}
                                                onClick={() => setIdSelectOpen(!idSelectOpen)}
                                                ref={cutIdAnchor}
                                                className={classes.inputOptionButton}>
                                                {TYPES[searchIDType].label}
                                                <ArrowDropDown style={{ fontSize: '15px' }} />
                                            </Button>
                                            <Menu
                                                open={idSelectOpen}
                                                anchorEl={cutIdAnchor.current}
                                                sx={{ marginLeft: '50px' }}>
                                                {Object.values(SearchIDType).map((option) => {
                                                    return (
                                                        <MenuItem
                                                            key={TYPES[option].label}
                                                            onClick={() => {
                                                                setSearchIDType(option);
                                                            }}>
                                                            {TYPES[option].label}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </Menu>
                                        </div>
                                    </ClickAwayListener>
                                }
                            />
                        </Tooltip>
                        <Button type='submit' sx={{ display: 'none' }} />
                    </FormControl>
                </form>
            </Box>

            <Box display={'flex'} flexDirection={'row'} gap={1} flex={1} width={'100%'} ml={1} flexWrap={'wrap'}>
                <MultiSelectFilter
                    disabled={Boolean(searchID)}
                    label={'Device Category'}
                    options={categoryData}
                    value={filters.category}
                    onChange={(value) => setFilter(CutFilterOptions.Category, value, true)}
                    getLabelValue={(option) => option}
                />

                <MultiSelectFilter
                    disabled={Boolean(searchID) || isSelected(CutFilterOptions.Status)}
                    label={'Cut Type'}
                    options={CutTypeOptions}
                    value={filters.type}
                    onChange={(value) => setFilter(CutFilterOptions.CutType, value, true)}
                    getLabelValue={(option) => option}
                />

                <MultiSelectFilter
                    disabled={Boolean(searchID) || !selectedCutType}
                    label={'Status'}
                    options={statusOptions}
                    value={filters.status}
                    onChange={(value) => setFilter(CutFilterOptions.Status, value, true)}
                    getLabelValue={(option) => option}
                />

                <MultiSelectFilter
                    disabled={Boolean(searchID)}
                    label={'Material'}
                    options={MaterialOptions}
                    value={filters.materialIds}
                    onChange={(value: { materialId: string; material: string }) =>
                        setFilter(CutFilterOptions.Material, value.materialId)
                    }
                    idKey={'materialId'}
                    getLabelValue={(option) => option.material}
                />

                <MultiSelectFilter
                    search
                    disabled={Boolean(searchID)}
                    label={'Country'}
                    options={countriesData}
                    value={filters.countryName}
                    onChange={(value) => setFilter(CutFilterOptions.Country, value)}
                    getLabelValue={(option) => option}
                />

                <MultiSelectFilter
                    search
                    disabled={Boolean(searchID) || shouldDisable(CutFilterOptions.Company, filters)}
                    idKey={'companyId'}
                    label={'Company'}
                    options={companyValues}
                    value={filters.companyIds}
                    onChange={(value: { companyId: string; companyName: string }) =>
                        setFilter(CutFilterOptions.Company, value.companyId)
                    }
                    getLabelValue={(option) => option.companyName}
                />

                <MultiSelectFilter
                    search
                    disabled={Boolean(searchID) || shouldDisable(CutFilterOptions.Reseller, filters)}
                    idKey={'resellerId'}
                    label={'Reseller'}
                    options={resellerValues}
                    value={filters.resellerIds}
                    onChange={(value: { resellerId: string; resellerName: string }) =>
                        setFilter(CutFilterOptions.Reseller, value.resellerId)
                    }
                    getLabelValue={(option) => option.resellerName}
                />

                <MultiSelectFilter
                    search
                    disabled={Boolean(searchID) || shouldDisable(CutFilterOptions.Location, filters)}
                    idKey={'locationId'}
                    label={'Location'}
                    options={locationValues}
                    value={filters.locationIds}
                    onChange={(value: { locationId: string; locationName: string }) =>
                        setFilter(CutFilterOptions.Location, value.locationId)
                    }
                    getLabelValue={(option) => option.locationName}
                />
            </Box>
        </div>
    );
};
